import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getSchedule, createSchedule, updateSchedule } from '../services/maintenanceService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';
import { useEquipmentGroups } from '../utils/equipmentGroups';

const MaintenanceScheduleForm = () => {
  const equipmentCategories = useEquipmentGroups();
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentLocation } = useLocationContext();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    equipment_categories: [],
    interval_days: 90,
    description: '',
    next_due_date: '',
    active: true
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchSchedule();
    }
  }, [id, currentLocation?.id]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const data = await getSchedule(id);
      setFormData({
        title: data.title,
        equipment_categories: data.equipment_category || [],
        interval_days: data.interval_days,
        description: data.description || '',
        next_due_date: data.next_due_date || '',
        active: data.active
      });
      setHistory(data.history || []);
    } catch (err) {
      setError('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'active') {
      setFormData(prev => ({ ...prev, active: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleCategory = (catValue) => {
    setFormData(prev => {
      const cats = prev.equipment_categories;
      if (cats.includes(catValue)) {
        return { ...prev, equipment_categories: cats.filter(c => c !== catValue) };
      } else {
        return { ...prev, equipment_categories: [...cats, catValue] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.interval_days || formData.interval_days < 1) {
      setError('Interval must be at least 1 day');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        title: formData.title,
        equipment_category: formData.equipment_categories.length > 0 ? formData.equipment_categories : null,
        equipment_area_id: null,
        interval_days: parseInt(formData.interval_days),
        description: formData.description,
        next_due_date: formData.next_due_date || null,
        active: formData.active
      };

      if (isEditing) {
        await updateSchedule(id, payload);
      } else {
        await createSchedule(payload);
      }
      navigate('/maintenance/schedules');
    } catch (err) {
      setError('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Schedule' : 'New Maintenance Schedule'}
        </h1>
        <button
          onClick={() => navigate('/maintenance/schedules')}
          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
        >
          Back to Schedules
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 overflow-hidden max-w-full">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. Oil Change, Replace Filters, Grease Fittings"
          />
        </div>

        {/* Equipment Groups */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Groups</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {equipmentCategories.map(cat => (
              <label
                key={cat.value}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  formData.equipment_categories.includes(cat.value)
                    ? 'bg-blue-50 border-blue-300 text-blue-800'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.equipment_categories.includes(cat.value)}
                  onChange={() => toggleCategory(cat.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium">{cat.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Select all equipment groups this maintenance applies to. Leave empty for general/all equipment.
          </p>
        </div>

        {/* Interval */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Interval (days)</label>
          <input
            type="number"
            name="interval_days"
            value={formData.interval_days}
            onChange={handleChange}
            min="1"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            Common intervals: 30 (monthly), 90 (quarterly), 180 (semi-annual), 365 (annual)
          </p>
        </div>

        {/* First Due Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isEditing ? 'Next Due Date' : 'First Due Date'}
          </label>
          <input
            type="date"
            name="next_due_date"
            value={formData.next_due_date}
            onChange={handleChange}
            style={{ WebkitAppearance: 'none', maxWidth: '100%', boxSizing: 'border-box', minHeight: '42px' }}
            className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Details about what this maintenance involves..."
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center">
          <input
            type="checkbox"
            name="active"
            id="active"
            checked={formData.active}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="active" className="ml-2 text-sm font-medium text-gray-700">
            Active (schedule will show up in dashboard alerts)
          </label>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Schedule' : 'Create Schedule')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/maintenance/schedules')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Completion History (only when editing) */}
      {isEditing && history.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Completion History</h2>
          </div>
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map(wo => (
                  <tr key={wo.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(wo.completed_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      ${(wo.parts_cost || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      ${(wo.labor_cost || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${(wo.total_cost || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {wo.performed_by || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/maintenance/work-order/${wo.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}
    </div>
  );
};

export default MaintenanceScheduleForm;
